import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {translations} from '../../../../i18n/translations';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {UserGender} from '../../../../backend/enums';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {PDF_FONT_MAIN_COLOR} from '../base/export-pdf-font';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedPdfExportFile, PreparedXlsxExportFile} from '../base/export-data-types';

export function prepareSeniorsListPDF({
  reportData,
  entity,
  t,
}: {
  reportData: any[];
  entity?: EntityInfo;
  t: TFunction;
}): PreparedPdfExportFile {
  const pdf = new jsPDF({orientation: 'landscape', compress: true});
  pdf.setProperties({
    title: `${t(translations.seniors.root.seniors)}`,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  const reportTitle = t(translations.role_permissions['seniors/list']);
  const tableStartY = pdfAddBodyTitle(pdf, {title: reportTitle});

  let table: any[] = [];

  for (let entry of reportData) {
    // user personal data
    const user = entry.userId;
    const name = user.displayName ?? user.name ?? '';
    const socialResponse = entry.socialResponses?.map((s: any) => s.abbreviation).join(', ') || '';
    const age = user.birthday ? moment().diff(user.birthday, 'years').toString() : '';
    // user affiliation data
    const affiliation = entry;
    const roomNumber = affiliation.room ?? '';
    const admissionDate = affiliation.admissionDate
      ? moment(affiliation.admissionDate).format('DD/MM/YYYY')
      : '';
    const departureDate = affiliation.departureDate
      ? moment(affiliation.departureDate).format('DD/MM/YYYY')
      : '';
    const processNumber = affiliation.processNumber ?? '';
    const recurringFee = affiliation.recurringFee ?? '';

    const status = user.isActive
      ? t(translations.seniors.root.state_value_true)
      : t(translations.seniors.root.state_value_false);

    const cell = (cellContent: string) => ({
      content: cellContent,
      // styles: {fillColor: 'white'},
    });

    table.push([
      cell(name),
      cell(processNumber),
      cell(socialResponse),
      cell(roomNumber),
      cell(recurringFee),
      cell(age),
      cell(status),
      cell(admissionDate),
      cell(departureDate),
    ]);
  }

  // Translations
  const nameStr = t(translations.office.professional_caregiver.seniors.table_view_screen.name);
  const socialResStr = t(
    translations.office.professional_caregiver.seniors.table_view_screen.social_response
  );
  const ageStr = t(translations.office.professional_caregiver.seniors.table_view_screen.age);
  const roomStr = t(translations.office.professional_caregiver.seniors.table_view_screen.room);
  const statusStr = t(translations.office.professional_caregiver.seniors.table_view_screen.status);
  const processNumberStr = t(translations.user_form.process_number_input);
  const admissionDateStr = t(translations.user_form.admission_date_input);
  const departureDateStr = t(translations.user_form.departure_date_input);
  const recurringFeeStr = t(translations.user_form.recurring_fee_input);

  autoTable(pdf, {
    body: table,
    head: [
      [
        nameStr,
        processNumberStr,
        socialResStr,
        roomStr,
        recurringFeeStr,
        ageStr,
        statusStr,
        admissionDateStr,
        departureDateStr,
      ],
    ],
    startY: tableStartY,
    styles: {fontSize: 9},
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    columnStyles: {
      0: {cellWidth: 30}, // Name
    },
    willDrawPage: function (data) {
      // ---- Add Header to first page ----
      if (data.pageNumber === 1) {
        pdfHeaderInfo(pdf, undefined, entity, t);

        // Reset custom margins for the rest of the pages
        data.settings.margin.top = 7;
        data.settings.margin.bottom = 15;
      }
    },
    didDrawPage: function (data) {
      // ---- Add Footer to all pages ----
      const pageCounter = data.pageNumber.toString();
      pdfFooterInfo(pdf, pageCounter);
    },
  });

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.seniors.root.title),
  ]);

  return {
    file: pdf,
    fileName,
  };
}

export function prepareSeniorsListXLSX({
  reportData,
  entity,
  t,
}: {
  reportData: any[];
  entity?: EntityInfo;
  t: TFunction;
}): PreparedXlsxExportFile {
  let rows: string[][] = [];

  for (let entry of reportData) {
    const user = entry.userId;
    const affiliation = entry;

    // user personal data
    const name = user.displayName ?? user.name ?? '';
    const fullName = user.name ?? '';
    const socialSecurityNumber = user.socialSecurityNumber?.toString() ?? '';
    const taxPayerNumber = user.taxPayerNumber?.toString() ?? '';
    const nationalHealthNumber = user.nationalHealthServiceNumber?.toString() ?? '';
    const nationalIdNumber = user.nationalIdNumber ?? '';
    const gender = user.gender ? t(translations.genders[user.gender as UserGender]) : '';
    const language = user.languageCode ?? '';
    const age = user.birthday ? moment().diff(user.birthday, 'years').toString() : '';
    const address = user.address ?? '';
    const phone = user.phoneNumber ?? '';
    const email = user.email ?? '';
    const region = user.region ?? '';
    const religion = user.religion ?? '';
    // const occupation = user.occupationId ?? '';
    // user affiliation data
    const socialResponse = entry.socialResponses?.map((s: any) => s.abbreviation).join(', ') || '';
    const roomNumber = affiliation.room ?? '';
    const admissionDate = affiliation.admissionDate
      ? moment(affiliation.admissionDate).format('DD/MM/YYYY')
      : '';
    const departureDate = affiliation.departureDate
      ? moment(affiliation.departureDate).format('DD/MM/YYYY')
      : '';
    const status = user.isActive
      ? t(translations.seniors.root.state_value_true)
      : t(translations.seniors.root.state_value_false);
    const additionalInfo = affiliation.additionalInfo ?? '';
    const recurringFee = affiliation.recurringFee ?? '';

    rows.push([
      name, // 1
      fullName, // 2
      socialResponse, // 3
      roomNumber, // 4
      age, // 5
      gender, // 6
      taxPayerNumber, // 7
      socialSecurityNumber, // 8
      nationalHealthNumber, // 9
      nationalIdNumber, // 10
      address, // 11
      phone, // 12
      email, // 13
      region, // 14
      language, // 15
      religion, // 16
      recurringFee, // 17
      additionalInfo, // 18
      admissionDate, // 19
      departureDate, // 20
      status, // 21
    ]);
  }

  // Translations
  const nameStr = t(translations.office.professional_caregiver.seniors.table_view_screen.name);
  const fullNameStr = t(translations.user_form.full_name_input);
  const admissionDateStr = t(translations.user_form.admission_date_input);
  const departureDateStr = t(translations.user_form.departure_date_input);
  const socialResStr = t(
    translations.office.professional_caregiver.seniors.table_view_screen.social_response
  );
  const ageStr = t(translations.office.professional_caregiver.seniors.table_view_screen.age);
  const roomStr = t(translations.office.professional_caregiver.seniors.table_view_screen.room);
  const statusStr = t(translations.office.professional_caregiver.seniors.table_view_screen.status);
  const genderStr = t(translations.seniors.root.gender_header);
  const taxPayerStr = t(translations.user_form.tax_payer_number_input);
  const socialSecurityNumberStr = t(translations.user_form.social_security_number_input);
  const nationalHealthNumberStr = t(translations.user_form.patient_number_input);
  const nationalIdNumberStr = t(translations.user_form.citizen_card_input);
  const addressStr = t(translations.user_form.address_input);
  const phoneStr = t(translations.user_form.mobile_phone_number_input);
  const emailStr = t(translations.user_form.email_input);
  const regionStr = t(translations.user_form.region_input);
  const religionStr = t(translations.user_form.religion_input);
  const languageStr = t(translations.user_form.language_input);
  // const occupationStr = t(translations.user_form.profession_input);
  const recurringFeeStr = t(translations.user_form.recurring_fee_input);
  const additionalInfoStr = t(translations.user_form.aditional_info_input);

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  // Create a new worksheet
  let worksheet = XLSX.utils.json_to_sheet(rows);
  // Add headers to worksheet
  worksheet = XLSX.utils.sheet_add_aoa(worksheet, [
    [
      nameStr, // 1
      fullNameStr, // 2
      socialResStr, // 3
      roomStr, // 4
      ageStr, // 5
      genderStr, // 6
      taxPayerStr, // 7
      socialSecurityNumberStr, // 8
      nationalHealthNumberStr, // 9
      nationalIdNumberStr, // 10
      addressStr, // 11
      phoneStr, // 12
      emailStr, // 13
      regionStr, // 14
      languageStr, // 15
      religionStr, // 16
      recurringFeeStr, // 17
      additionalInfoStr, // 18
      admissionDateStr, // 19
      departureDateStr, // 20
      statusStr, // 21
    ],
  ]);
  // Set columns width
  worksheet['!cols'] = [
    {wch: 20}, // 1
    {wch: 30}, // 2
    {wch: 15}, // 3
    {wch: 10}, // 4
    {wch: 10}, // 5
    {wch: 10}, // 6
    {wch: 15}, // 7
    {wch: 15}, // 8
    {wch: 15}, // 9
    {wch: 15}, // 10
    {wch: 15}, // 11
    {wch: 20}, // 12
    {wch: 20}, // 13
    {wch: 10}, // 14
    {wch: 10}, // 15
    {wch: 10}, // 16
    {wch: 10}, // 17
    {wch: 50}, // 18
    {wch: 15}, // 19
    {wch: 15}, // 20
    {wch: 15}, // 21
  ];
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet);

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.seniors.root.title),
  ]);

  return {
    file: workbook,
    fileName,
  };
}
